import type { ActionCreatorWithoutPayload, ActionCreatorWithPayload } from '@reduxjs/toolkit';
import type { QueryArgFrom, QueryDefinition, ResultTypeFrom } from '@reduxjs/toolkit/query';
import type {
  LazyQueryTrigger,
  UseLazyQueryLastPromiseInfo,
  UseQueryStateResult,
} from '@reduxjs/toolkit/src/query/react/buildHooks';
import { useCallback, useEffect } from 'react';

import { useStoreDispatch } from '@hook/store/store.hooks';
import type { BaseStoreInterface } from '@interface/store/base-store.interface';
import { UNLOADED_STATUS } from '@util/constants/api.constants';

interface ReducerActionsInterface {
  startRequest: ActionCreatorWithoutPayload;
  setLoaded: ActionCreatorWithPayload<boolean>;
  endRequest: ActionCreatorWithPayload<Pick<BaseStoreInterface, 'error'>>;
}

export const useLazyQuery = <D extends QueryDefinition<any, any, any, any>>(
  query: [LazyQueryTrigger<D>, UseQueryStateResult<D, any>, UseLazyQueryLastPromiseInfo<D>],
  actions: ReducerActionsInterface,
  setData: ActionCreatorWithPayload<ResultTypeFrom<D>>,
) => {
  const dispatch = useStoreDispatch();
  const [call, state] = query;

  useEffect(() => void dispatch(actions.setLoaded(!UNLOADED_STATUS.has(state.status))), [state.status]);

  return {
    action: useCallback(
      (data: QueryArgFrom<D>, preferCacheValue?: boolean) => {
        dispatch(actions.startRequest());

        return call(data, preferCacheValue)
          .unwrap()
          .then((result) => {
            dispatch(setData(result));
            dispatch(actions.endRequest({ error: null }));
            return result;
          })
          .catch((error: any) => {
            dispatch(actions.endRequest({ error: error.data }));
            return error;
          });
      },
      [dispatch, call, actions, setData],
    ),
    state,
  };
};
